/**
 * ProviderHealthCard Component
 *
 * Compact summary card for provider-level daily breakdown data.
 * Replaces the raw DailyBreakdownTable at provider level with:
 * - Selector summary with alignment status
 * - Issues & actions (if any)
 * - Collapsible daily timeline (DailyBreakdownTable behind disclosure)
 */

import { useState } from "react";
import { AlertTriangle, CheckCircle2, XCircle, ChevronDown, ChevronRight, Key } from "lucide-react";
import type { DailyDetail } from "@/lib/api";
import { DailyBreakdownTable, exportDailyDetailCsv } from "./SourceDetailsPanel";

interface ProviderHealthCardProps {
  dailyDetail: DailyDetail[];
  providerName?: string;
  onExportCsv?: () => void;
}

interface SelectorSummary {
  selector: string;
  dkimDomain: string | null;
  aligned: boolean;
  headerFrom: string | null;
  totalVolume: number;
  passRate: number;
}

interface Issue {
  type: "error" | "warning";
  message: string;
  detail?: string;
}

function computeSummary(dailyDetail: DailyDetail[]) {
  // Aggregate selectors across all days
  const selectorMap = new Map<string, {
    selector: string;
    dkimDomain: string | null;
    pass: number;
    fail: number;
    total: number;
  }>();
  const headerFromSet = new Set<string>();
  const envelopeFromSet = new Set<string>();
  let totalMessages = 0;
  let totalSpfPass = 0;
  let totalSpfFail = 0;
  let totalDkimPass = 0;
  let totalDkimFail = 0;
  let totalDmarcPass = 0;
  let totalDmarcFail = 0;

  for (const day of dailyDetail) {
    totalMessages += day.total_messages;
    totalSpfPass += day.spf_pass;
    totalSpfFail += day.spf_fail;
    totalDmarcPass += day.dmarc_pass ?? 0;
    totalDmarcFail += day.dmarc_fail ?? 0;

    for (const hf of day.header_from ?? []) {
      headerFromSet.add(hf.toLowerCase());
    }
    for (const ef of day.envelope_from ?? []) {
      envelopeFromSet.add(ef.toLowerCase());
    }

    for (const sel of day.selectors) {
      const existing = selectorMap.get(sel.selector);
      if (existing) {
        existing.pass += sel.pass;
        existing.fail += sel.fail;
        existing.total += sel.total;
        if (sel.dkim_domain && !existing.dkimDomain) {
          existing.dkimDomain = sel.dkim_domain;
        }
      } else {
        selectorMap.set(sel.selector, {
          selector: sel.selector,
          dkimDomain: sel.dkim_domain ?? null,
          pass: sel.pass,
          fail: sel.fail,
          total: sel.total,
        });
      }
      totalDkimPass += sel.pass;
      totalDkimFail += sel.fail;
    }
  }

  // Build selector summaries with alignment info
  const selectors: SelectorSummary[] = Array.from(selectorMap.values())
    .map((s) => {
      const aligned = !s.dkimDomain || headerFromSet.has(s.dkimDomain.toLowerCase());
      return {
        selector: s.selector,
        dkimDomain: s.dkimDomain,
        aligned,
        headerFrom: aligned ? null : Array.from(headerFromSet).join(", ") || null,
        totalVolume: s.total,
        passRate: s.total > 0 ? (s.pass / s.total) * 100 : 0,
      };
    })
    .sort((a, b) => b.totalVolume - a.totalVolume);

  // Compute rates
  const spfTotal = totalSpfPass + totalSpfFail;
  const spfRate = spfTotal > 0 ? (totalSpfPass / spfTotal) * 100 : null;
  const dkimTotal = totalDkimPass + totalDkimFail;
  const dkimRate = dkimTotal > 0 ? (totalDkimPass / dkimTotal) * 100 : null;
  const dmarcTotal = totalDmarcPass + totalDmarcFail;
  const dmarcRate = dmarcTotal > 0 ? (totalDmarcPass / dmarcTotal) * 100 : null;

  // Detect issues
  const issues: Issue[] = [];
  const misaligned = selectors.filter((s) => !s.aligned);
  if (misaligned.length > 0) {
    for (const sel of misaligned) {
      issues.push({
        type: "error",
        message: `DKIM misalignment — selector "${sel.selector}" signs as ${sel.dkimDomain}`,
        detail: sel.headerFrom ? `Header From: ${sel.headerFrom}` : undefined,
      });
    }
  }
  if (spfRate !== null && spfRate < 95) {
    issues.push({
      type: spfRate < 80 ? "error" : "warning",
      message: `SPF passing at ${spfRate.toFixed(1)}% (${totalSpfFail.toLocaleString()} failures)`,
    });
  }
  if (dkimRate !== null && dkimRate < 95) {
    issues.push({
      type: dkimRate < 80 ? "error" : "warning",
      message: `DKIM passing at ${dkimRate.toFixed(1)}% (${totalDkimFail.toLocaleString()} failures)`,
    });
  }
  if (selectors.length === 0 && totalMessages > 0) {
    issues.push({
      type: "warning",
      message: "No DKIM signing detected for this provider",
    });
  }

  // Enforcement readiness
  const allAligned = misaligned.length === 0;
  const spfGood = spfRate === null || spfRate >= 95;
  const dkimGood = dkimRate === null || dkimRate >= 95;
  const readiness: "ready" | "issues" | "blocking" =
    allAligned && spfGood && dkimGood ? "ready" :
    (!allAligned || (spfRate !== null && spfRate < 80) || (dkimRate !== null && dkimRate < 80)) ? "blocking" :
    "issues";

  return {
    selectors,
    issues,
    readiness,
    totalMessages,
    spfRate,
    dkimRate,
    dmarcRate,
    dayCount: dailyDetail.length,
  };
}

export function ProviderHealthCard({ dailyDetail, providerName, onExportCsv }: ProviderHealthCardProps) {
  const [timelineOpen, setTimelineOpen] = useState(false);
  const summary = computeSummary(dailyDetail);

  if (dailyDetail.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      {/* Section A: Selector Summary */}
      <div className="px-4 py-3 border-b border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">DKIM Selectors</span>
            <span className="text-xs text-slate-400">({summary.dayCount} days)</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {summary.spfRate !== null && (
              <span className={summary.spfRate >= 95 ? "text-emerald-600" : summary.spfRate >= 80 ? "text-amber-600" : "text-red-600"}>
                SPF {summary.spfRate.toFixed(0)}%
              </span>
            )}
            {summary.dkimRate !== null && (
              <span className={summary.dkimRate >= 95 ? "text-emerald-600" : summary.dkimRate >= 80 ? "text-amber-600" : "text-red-600"}>
                DKIM {summary.dkimRate.toFixed(0)}%
              </span>
            )}
            {summary.dmarcRate !== null && (
              <span className={summary.dmarcRate >= 95 ? "text-emerald-600" : summary.dmarcRate >= 80 ? "text-amber-600" : "text-red-600"}>
                DMARC {summary.dmarcRate.toFixed(0)}%
              </span>
            )}
          </div>
        </div>

        {summary.selectors.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {summary.selectors.map((sel) => (
              <div
                key={sel.selector}
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border ${
                  sel.aligned
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                {sel.aligned ? (
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                ) : (
                  <XCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
                )}
                <span className="font-mono truncate max-w-[180px]" title={sel.selector}>
                  {sel.selector}
                </span>
                {sel.dkimDomain && !sel.aligned && (
                  <span className="text-red-500 text-[10px]" title={`Signs as ${sel.dkimDomain}`}>
                    ({sel.dkimDomain})
                  </span>
                )}
                <span className="text-slate-400">{sel.totalVolume.toLocaleString()}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic">No DKIM selectors detected</div>
        )}
      </div>

      {/* Section B: Issues & Actions */}
      {summary.issues.length > 0 && (
        <div className="px-4 py-2.5 border-b border-slate-100 bg-amber-50/30">
          <div className="space-y-1.5">
            {summary.issues.map((issue, i) => (
              <div key={i} className="flex items-start gap-2 text-xs">
                {issue.type === "error" ? (
                  <XCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <span className={issue.type === "error" ? "text-red-700" : "text-amber-700"}>
                    {issue.message}
                  </span>
                  {issue.detail && (
                    <span className="text-slate-500 ml-1">— {issue.detail}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section C: Collapsible Daily Timeline */}
      <div className="px-4 py-2">
        <button
          onClick={() => setTimelineOpen(!timelineOpen)}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 w-full"
        >
          {timelineOpen ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <span className="font-medium">Daily Timeline</span>
          <span className="text-xs text-slate-400">
            {summary.totalMessages.toLocaleString()} msgs over {summary.dayCount} days
          </span>
        </button>
        {timelineOpen && (
          <div className="mt-1 -mx-4">
            <DailyBreakdownTable
              dailyDetail={dailyDetail}
              onExportCsv={onExportCsv ?? (() => exportDailyDetailCsv(dailyDetail, providerName || "provider"))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Compute enforcement readiness for a provider from daily detail data.
 * Returns "ready" | "issues" | "blocking"
 */
export function computeEnforcementReadiness(dailyDetail: DailyDetail[]): "ready" | "issues" | "blocking" {
  if (dailyDetail.length === 0) return "ready";
  return computeSummary(dailyDetail).readiness;
}
